import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiCheck, FiX, FiSend } = FiIcons;

const StoryQuestion = ({ node, studentName, onAnswer }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // Initialize timer
  useEffect(() => {
    if (node.data.timer) {
      setTimeLeft(node.data.timer);
    }
  }, [node.data.timer]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - handle as wrong answer
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleTimeUp = () => {
    // Find the incorrect answer path based on question type
    let nextNodeId = null;
    
    if (node.data.type === 'multiple-choice') {
      const incorrectChoice = node.data.choices?.find(choice => !choice.isCorrect);
      nextNodeId = incorrectChoice?.nextNodeId;
    } else if (node.data.type === 'true-false') {
      // Go to the incorrect path
      const correctAnswer = node.data.correctAnswer;
      nextNodeId = correctAnswer 
        ? node.data.falseNextNodeId 
        : node.data.trueNextNodeId;
    } else if (node.data.type === 'short-answer') {
      // Go to incorrect path for short answer
      nextNodeId = node.data.incorrectNextNodeId;
    }

    if (nextNodeId) {
      onAnswer(nextNodeId);
    }
  };

  const handleAnswerSelect = (answer) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    
    let correct = false;
    let nextNodeId = null;

    if (node.data.type === 'multiple-choice') {
      const choice = node.data.choices.find(c => c.id === answer);
      correct = choice?.isCorrect || false;
      nextNodeId = choice?.nextNodeId;
    } else if (node.data.type === 'true-false') {
      correct = answer === node.data.correctAnswer;
      nextNodeId = answer 
        ? node.data.trueNextNodeId 
        : node.data.falseNextNodeId;
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    // Auto-advance after showing feedback
    setTimeout(() => {
      if (nextNodeId) {
        onAnswer(nextNodeId);
      }
    }, 1500);
  };

  const handleShortAnswerSubmit = () => {
    if (showFeedback) return;

    const studentAnswer = textAnswer.trim();
    let correct = false;
    
    if (studentAnswer) {
      // Check against all correct answers
      const correctAnswers = node.data.correctAnswers || [];
      
      correct = correctAnswers.some(correctAnswer => {
        if (!correctAnswer.trim()) return false;
        
        if (node.data.ignoreCase) {
          return studentAnswer.toLowerCase() === correctAnswer.trim().toLowerCase();
        } else {
          return studentAnswer === correctAnswer.trim();
        }
      });
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    // Auto-advance after showing feedback
    setTimeout(() => {
      const nextNodeId = correct 
        ? node.data.correctNextNodeId 
        : node.data.incorrectNextNodeId;
      
      if (nextNodeId) {
        onAnswer(nextNodeId);
      }
    }, 1500);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  const embedUrl = getYouTubeEmbedUrl(node.data.videoUrl);

  return (
    <motion.div
      className="max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="text-white p-6" style={{ backgroundColor: '#2b6777' }}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold font-rubik">Hallo, {studentName}!</h2>
              <p className="text-white/90 font-rubik">Beantworten Sie die folgende Frage</p>
            </div>
            {timeLeft !== null && timeLeft > 0 && (
              <div className="flex items-center space-x-2 px-4 py-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <SafeIcon icon={FiClock} className="w-5 h-5" />
                <span className="font-mono text-lg">{timeLeft}s</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Media Content */}
          {(node.data.imageUrl || embedUrl) && (
            <div className="mb-6">
              {node.data.imageUrl && (
                <img
                  src={node.data.imageUrl}
                  alt="Fragenillustration"
                  className="w-full max-w-md mx-auto rounded-lg shadow-md"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              {embedUrl && (
                <div className="aspect-video max-w-2xl mx-auto">
                  <iframe
                    src={embedUrl}
                    className="w-full h-full rounded-lg"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          )}

          {/* Question */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 text-center font-rubik" style={{ color: '#2b6777' }}>
              {node.data.questionText}
            </h3>
          </div>

          {/* Answer Options */}
          <div className="space-y-4">
            {/* Multiple Choice */}
            {node.data.type === 'multiple-choice' && (
              <>
                {node.data.choices?.map((choice, index) => (
                  <motion.button
                    key={choice.id}
                    onClick={() => handleAnswerSelect(choice.id)}
                    disabled={showFeedback}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all font-rubik ${
                      selectedAnswer === choice.id
                        ? showFeedback
                          ? isCorrect
                            ? 'border-green-500 bg-green-50 text-green-800'
                            : 'border-red-500 bg-red-50 text-red-800'
                          : 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    } ${showFeedback ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{ 
                      color: selectedAnswer === choice.id && showFeedback ? undefined : '#2b6777' 
                    }}
                    whileHover={!showFeedback ? { scale: 1.02 } : {}}
                    whileTap={!showFeedback ? { scale: 0.98 } : {}}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{choice.text}</span>
                      {selectedAnswer === choice.id && showFeedback && (
                        <SafeIcon 
                          icon={isCorrect ? FiCheck : FiX} 
                          className={`w-6 h-6 ${isCorrect ? 'text-green-600' : 'text-red-600'}`} 
                        />
                      )}
                    </div>
                  </motion.button>
                ))}
              </>
            )}

            {/* True/False */}
            {node.data.type === 'true-false' && (
              <>
                <motion.button
                  onClick={() => handleAnswerSelect(true)}
                  disabled={showFeedback}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all font-rubik ${
                    selectedAnswer === true
                      ? showFeedback
                        ? isCorrect
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-red-500 bg-red-50 text-red-800'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${showFeedback ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  style={{ 
                    color: selectedAnswer === true && showFeedback ? undefined : '#2b6777' 
                  }}
                  whileHover={!showFeedback ? { scale: 1.02 } : {}}
                  whileTap={!showFeedback ? { scale: 0.98 } : {}}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-lg">Wahr</span>
                    {selectedAnswer === true && showFeedback && (
                      <SafeIcon 
                        icon={isCorrect ? FiCheck : FiX} 
                        className={`w-6 h-6 ${isCorrect ? 'text-green-600' : 'text-red-600'}`} 
                      />
                    )}
                  </div>
                </motion.button>

                <motion.button
                  onClick={() => handleAnswerSelect(false)}
                  disabled={showFeedback}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all font-rubik ${
                    selectedAnswer === false
                      ? showFeedback
                        ? isCorrect
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-red-500 bg-red-50 text-red-800'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${showFeedback ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  style={{ 
                    color: selectedAnswer === false && showFeedback ? undefined : '#2b6777' 
                  }}
                  whileHover={!showFeedback ? { scale: 1.02 } : {}}
                  whileTap={!showFeedback ? { scale: 0.98 } : {}}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-lg">Falsch</span>
                    {selectedAnswer === false && showFeedback && (
                      <SafeIcon 
                        icon={isCorrect ? FiCheck : FiX} 
                        className={`w-6 h-6 ${isCorrect ? 'text-green-600' : 'text-red-600'}`} 
                      />
                    )}
                  </div>
                </motion.button>
              </>
            )}

            {/* Short Answer */}
            {node.data.type === 'short-answer' && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={textAnswer}
                    onChange={(e) => setTextAnswer(e.target.value)}
                    disabled={showFeedback}
                    maxLength={255}
                    className={`flex-1 px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all font-rubik ${
                      showFeedback
                        ? isCorrect
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-red-500 bg-red-50 text-red-800'
                        : 'border-gray-300 focus:border-gray-400'
                    } ${showFeedback ? 'cursor-not-allowed' : ''}`}
                    style={{ 
                      color: showFeedback ? undefined : '#2b6777' 
                    }}
                    placeholder="Geben Sie Ihre Antwort hier ein..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !showFeedback && textAnswer.trim()) {
                        handleShortAnswerSubmit();
                      }
                    }}
                  />
                  <motion.button
                    onClick={handleShortAnswerSubmit}
                    disabled={showFeedback || !textAnswer.trim()}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors font-rubik ${
                      showFeedback || !textAnswer.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'text-white'
                    }`}
                    style={
                      !showFeedback && textAnswer.trim() 
                        ? { backgroundColor: '#c8d8e4' } 
                        : {}
                    }
                    whileHover={!showFeedback && textAnswer.trim() ? { scale: 1.02 } : {}}
                    whileTap={!showFeedback && textAnswer.trim() ? { scale: 0.98 } : {}}
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                  </motion.button>
                </div>

                {showFeedback && (
                  <div className="flex items-center justify-end">
                    <SafeIcon 
                      icon={isCorrect ? FiCheck : FiX} 
                      className={`w-6 h-6 ${isCorrect ? 'text-green-600' : 'text-red-600'}`} 
                    />
                  </div>
                )}

                <div className="text-sm text-center font-rubik" style={{ color: '#6b7280' }}>
                  {node.data.ignoreCase 
                    ? 'Antwort ist nicht case-sensitiv' 
                    : 'Antwort ist case-sensitiv'
                  }
                </div>
              </div>
            )}
          </div>

          {/* Feedback */}
          {showFeedback && (
            <motion.div
              className={`mt-6 p-4 rounded-lg ${
                isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon 
                  icon={isCorrect ? FiCheck : FiX} 
                  className="w-5 h-5" 
                />
                <span className="font-medium font-rubik">
                  {isCorrect ? 'Richtig!' : 'Falsch'}
                </span>
              </div>
              <p className="mt-1 text-sm font-rubik">
                {isCorrect 
                  ? 'Großartig! Weiter zum nächsten Teil der Geschichte...' 
                  : 'Keine Sorge, die Geschichte geht weiter...'}
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default StoryQuestion;